import {
  useState,
  useEffect,
  createContext,
  useMemo,
  useCallback,
  useContext,
} from "react";
import axios from "axios";
import { ROOT_SERVER, MATERIAL_URL, BRAND_INFO_URLS } from "../utilities/Static";
import SortFunction from "../utilities/SortFunction";
import { collectBrandArticleSlugs } from "../utilities/brandData";
import {
  canFilterBrandArticles,
  getBrandArticleFilterLabel,
  getSearchCustomerBrand,
  getSessionCustomerBrand,
} from "../utilities/loginAccount";
import { UserContext } from "./UserContext";

import { useQuery } from "@tanstack/react-query";

export const MaterialContext = createContext();

const MaterialContextProvider = ({ children }) => {
  const { userInformation } = useContext(UserContext);
  const [nikeData, setNikeData] = useState([]);
  const [search, setSearch] = useState("");
  ///Filters
  const [isFilter, setIsFilter] = useState(false);
  const [priceRange, setPriceRange] = useState([{ min: 1.8, max: 2.5 }]);
  const [status, setStatus] = useState("commercial");
  const [orderBy, setOrderBy] = useState("modified");
  const [brandArticlesOnly, setBrandArticlesOnly] = useState(false);

  const { isError, isSuccess, data, refetch } = useQuery({
    queryKey: ["materials"],
    queryFn: () => axios.get(MATERIAL_URL),
  });

  const allMaterials = useMemo(() => {
    return Array.isArray(data?.data) ? data.data : [];
  }, [data]);

  const materialsLoaded = !isError && isSuccess;

  const sessionBrand = getSessionCustomerBrand(userInformation);
  const brandArticleSlugs = useMemo(
    () => collectBrandArticleSlugs(nikeData, sessionBrand),
    [nikeData, sessionBrand],
  );
  const brandArticleFilterAvailable =
    canFilterBrandArticles(userInformation) && brandArticleSlugs.size > 0;
  const brandArticleFilterLabel = getBrandArticleFilterLabel(userInformation);
  const searchBrand = getSearchCustomerBrand(search, sessionBrand);

  const materials = useMemo(() => {
    const useBrandSlugs =
      (brandArticlesOnly && brandArticleFilterAvailable) || Boolean(searchBrand);
    const brandSlugs = useBrandSlugs
      ? collectBrandArticleSlugs(nikeData, searchBrand || sessionBrand)
      : null;

    return SortFunction(
      searchBrand ? "" : search,
      allMaterials,
      orderBy,
      priceRange,
      useBrandSlugs ? "none" : status,
      brandSlugs,
    );
  }, [
    search,
    searchBrand,
    allMaterials,
    orderBy,
    priceRange,
    status,
    brandArticlesOnly,
    brandArticleFilterAvailable,
    nikeData,
    sessionBrand,
  ]);

  const materialById = useMemo(() => {
    const map = new Map();
    allMaterials.forEach((material) => {
      if (material?.id != null) {
        map.set(material.id, material);
      }
    });
    return map;
  }, [allMaterials]);

  const nikeDataBySlug = useMemo(() => {
    const map = new Map();
    nikeData.forEach((item) => {
      if (!item?.slug) {
        return;
      }

      const existing = map.get(item.slug);
      if (existing) {
        existing.push(item);
      } else {
        map.set(item.slug, [item]);
      }
    });
    return map;
  }, [nikeData]);

  useEffect(() => {
    Promise.all(
      BRAND_INFO_URLS.map((url) =>
        axios.get(url).then((response) => response.data).catch(() => []),
      ),
    ).then((chunks) => {
      setNikeData(
        chunks.flatMap((chunk) => (Array.isArray(chunk) ? chunk : [])),
      );
    });
  }, []);

  useEffect(() => {
    if (!brandArticleFilterAvailable && brandArticlesOnly) {
      setBrandArticlesOnly(false);
    }
  }, [brandArticleFilterAvailable, brandArticlesOnly]);

  const changeStatus = useCallback((value) => {
    setStatus(value);
  }, []);

  const changeSearch = useCallback((value) => {
    setSearch(value);
  }, []);

  const changeOrderBy = useCallback((value) => {
    setOrderBy(value);
  }, []);

  const changePriceRange = useCallback((value) => {
    setPriceRange(value);
  }, []);

  const changeIsFilter = useCallback((value) => {
    setIsFilter(value);
  }, []);

  const changeBrandArticlesOnly = useCallback((value) => {
    setBrandArticlesOnly(Boolean(value));
  }, []);

  const value = useMemo(
    () => ({
      allMaterials,
      materialById,
      materialsLoaded,
      nikeData,
      nikeDataBySlug,
      ROOT_SERVER,
      materials,
      status,
      refetch,
      changeStatus,
      search,
      changeSearch,
      orderBy,
      changeOrderBy,
      priceRange,
      changePriceRange,
      isFilter,
      changeIsFilter,
      brandArticlesOnly,
      brandArticleFilterAvailable,
      brandArticleFilterLabel,
      changeBrandArticlesOnly,
    }),
    [
      allMaterials,
      materialById,
      materialsLoaded,
      nikeData,
      nikeDataBySlug,
      materials,
      status,
      refetch,
      changeStatus,
      search,
      changeSearch,
      orderBy,
      changeOrderBy,
      priceRange,
      changePriceRange,
      isFilter,
      changeIsFilter,
      brandArticlesOnly,
      brandArticleFilterAvailable,
      brandArticleFilterLabel,
      changeBrandArticlesOnly,
    ],
  );

  return (
    <MaterialContext.Provider value={value}>
      {children}
    </MaterialContext.Provider>
  );
};

export default MaterialContextProvider;
